import { css } from '@adornis/chemistry/directives/css.js';
import { allTinyMCEPlugins } from '@adornis/writer/x-tinymce.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { campusTinyPluginJumpMarks } from '../../components/tinyPlugins/campus-tiny-plugin-jumpmarks.js';
import { campusTinyPluginVideolyser } from '../../components/tinyPlugins/campus-tiny-plugin-videolyser.js';
import { CampusComponentExerciseEdit } from './campus-page-exercise-edit.js';

/**
 * @customElement campus-page-exercise-edit-text
 */
@customElement('campus-page-exercise-edit-text')
export class CampusPageExerciseEditText extends CampusComponentExerciseEdit {
  static override exerciseType: number = ExerciseType.FREITEXT;
  static override exerciseName: string = 'Freitext Aufgabe';
  static override exerciseElement: string = 'campus-page-exercise-edit-text';

  protected override getExcersiseType(): number {
    return CampusPageExerciseEditText.exerciseType;
  }

  protected override getExcersieName(): string {
    return CampusPageExerciseEditText.exerciseName;
  }

  constructor() {
    super();
  }

  private _editor() {
    return html`<cr-card space="md" padding="md">
      <chancen-component-card-title>Editor</chancen-component-card-title>
      <cr-flex space="md" space-between>
        <cr-flex horizontal crossaxis-center space="md">
          <cr-input flex ${this.form.field('title')} placeholder="Titel"></cr-input>
          <cr-icon ${css({ fontSize: '1.3rem', cursor: 'pointer' })}>
            info
            <cr-tooltip .text=${html`<cr-text> Der Titel wird versteckt wenn er leer gelassen wird </cr-text>`}>
            </cr-tooltip>
          </cr-icon>
        </cr-flex>
        <cr-card space="sm">
          <cr-text>Info: Die LaTeX-Ausdrücke müssen zwischen zwei $ stehen</cr-text>
          <cr-text>Bsp: $\\int \\dfrac{(log\\ x)^3}{x}\\ dx$</cr-text>
        </cr-card>
        <x-tinymce
          menubar="false"
          editable
          minheight="13vh"
          placeholder="Antwort"
          .editorOptions=${{
            convert_urls: false,
            plugins: [...allTinyMCEPlugins, 'campus-tiny-plugin-jumpmarks', 'campus-tiny-plugin-videolyser'],
            toolbar:
              'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent lineheight | numlist bullist | forecolor backcolor removeformat | charmap emoticons | fullscreen | campus-tiny-plugin-videolyser campus-tiny-plugin-jumpmarks insertfile image media template link anchor codesample | ltr rtl | table tablemergecells',
          }}
          .customPlugins=${[campusTinyPluginJumpMarks, campusTinyPluginVideolyser]}
          ${this.form.field('questionText')}
          @value-picked=${() => this.requestUpdate()}
        ></x-tinymce>
      </cr-flex>
    </cr-card>`;
  }

  private _answer() {
    return html`<cr-card space="md" padding="md">
      <chancen-component-card-title>Antworteinstellungen</chancen-component-card-title>
      <cr-card space="sm">
        <cr-text>
          Info: Das richtige Ergebniss muss zwischen zwei $ stehen und mit , getrennt werden. Bsp: x=$10$, y=$15$
        </cr-text>
      </cr-card>
      <cr-writer
        minheight="5vh"
        ${this.form.field('questionAnswerVariableFields')}
        placeholder="Optionale Variablen Felder"
      ></cr-writer>
      <cr-writer minheight="5vh" ${this.form.field('questionAnswer')} placeholder="Freitext Musterlösung"></cr-writer>
      <cr-flex horizontal crossaxis-center space="md">
        <cr-input
          flex
          ${this.form.field('minAnswerLength')}
          placeholder="Mindestlänge der Freitextantwort (Zeichen)"
          type="number"
        ></cr-input>
        <cr-icon ${css({ fontSize: '1.3rem', cursor: 'pointer' })}>
          info
          <cr-tooltip
            .text=${html`
              <cr-text>
                Eine Mindestlänge von -1 versteckt die Antwortmöglichkeit des Freitextes im "Play-Mode"
              </cr-text>
            `}
          >
          </cr-tooltip>
        </cr-icon>
      </cr-flex>
    </cr-card>`;
  }

  private _preview() {
    return html`<cr-card space="md" padding="md">
      <chancen-component-card-title>Vorschau</chancen-component-card-title>
      ${this.exercise.value?._id
        ? html`
            <campus-component-exercise-text
              .mode=${'preview'}
              .exerciseID="${this.exercise.value._id || ''}"
              .lessonID=${this.lesson.value?._id || ''}
            >
            </campus-component-exercise-text>
          `
        : html`
            <campus-component-exercise-text
              .mode=${'preview'}
              .exercisePreSave=${this.exercise.value}
              .lessonID=${this.lesson.value?._id || ''}
            >
            </campus-component-exercise-text>
          `}
    </cr-card>`;
  }

  override content() {
    return html` ${this.errorMsg.value
        ? html`<cr-text ${css({ color: 'red' })}>${this.errorMsg.value}</cr-text>`
        : nothing}
      <cr-grid columns="1fr 1fr" space="md" padding="sm">
        <cr-flex space="md"> ${this._editor()} ${this._answer()} </cr-flex>
        <cr-flex> ${this._preview()} </cr-flex>
      </cr-grid>`;
  }
}
